/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Heartbeat, FirstAid, Pill, Syringe, Stethoscope, UsersThree,
  ClipboardText, ShieldCheck, ArrowRight, Baby, PersonSimple, Dog,
  CalendarCheck, PhoneCall, Truck, Certificate,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, FeatureCard, BulletList,
  CtaBanner, BTN_GREEN,
} from "./_shared";

const SERVICES = [
  { Icon: Stethoscope, title: "Doctors on demand", desc: "Verified GPs, paediatricians and specialists — home or clinic. Prescriptions on-app." },
  { Icon: Syringe,     title: "Home nursing",     desc: "Trained caregivers for wound care, post-op recovery, IV therapy and injections." },
  { Icon: FirstAid,    title: "Ambulance & SOS",  desc: "Emergency dispatch with live tracking, first-aid instructions and hospital hand-off." },
  { Icon: Pill,        title: "Medicine delivery", desc: "Curated pharmacies with instant reminders, refill automation and doorstep delivery." },
  { Icon: Baby,        title: "Mother & baby",    desc: "Pregnancy tracker, kick counter, immunisation calendar and paediatric visits." },
  { Icon: PersonSimple,title: "Elder care",       desc: "Daily wellness rounds, vitals monitoring, medicine adherence and companion visits." },
  { Icon: Dog,         title: "Pet health",       desc: "Vet consults, vaccination reminders, grooming and health records — all in one place." },
  { Icon: ClipboardText,title:"Health records",   desc: "Family health vault — prescriptions, reports and vitals synced across every visit." },
];

const REMINDERS = [
  "Pill & dosage reminders with voice announcements",
  "Vaccination calendars for every family member",
  "Menstrual cycle & PMS predictions",
  "Pregnancy milestones with kick counter",
  "Pet vaccine & grooming schedules",
  "Emergency SOS with location share",
];

export default function HealthLanding() {
  useEffect(() => {
    document.title = "Family Health Services · Rescigo Health";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="page-health">
      <MarketingNav/>

      {/* HERO — mobile-first, responsive grid */}
      <section className="relative pt-28 md:pt-36 pb-14 md:pb-24 overflow-hidden bg-gradient-to-b from-[#FEE2E2] via-[#FCE7F3]/40 to-white">
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 80% 40%, rgba(220,38,38,0.08) 0%, transparent 55%)",
        }}/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-6 lg:px-12 grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <Eyebrow color="#DC2626">Family Health · Every Day</Eyebrow>
            <h1 className="font-heading text-[36px] leading-[1.08] sm:text-5xl md:text-6xl mt-4 tracking-tight text-slate-900">
              Health for your <span className="text-[#DC2626] italic">whole family</span>, in one app.
            </h1>
            <p className="text-base md:text-lg text-slate-600 mt-5 md:mt-6 leading-relaxed max-w-xl">
              Doctors, nurses, medicines, ambulances and reminders — verified
              partners across Kerala. Care that meets your family where they are.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="health-cta-register" className={BTN_GREEN}>
                Get the app <ArrowRight size={14} weight="bold"/>
              </Link>
              <Link to="/contact" data-testid="health-cta-contact"
                    className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-300 text-slate-800 hover:border-slate-400 text-sm font-medium">
                Talk to us
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3 md:gap-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck size={13} weight="fill" className="text-[#DC2626]"/>Verified partners</span>
              <span className="hidden md:inline">·</span>
              <span>24×7 support</span>
              <span className="hidden md:inline">·</span>
              <span>All ages · All species</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                       transition={{ duration: 0.7 }}
                       className="relative order-first md:order-last">
            <div className="bg-white rounded-2xl md:rounded-[28px] shadow-2xl ring-1 ring-slate-200 p-5 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#DC2626] text-white grid place-items-center">
                  <Heartbeat size={18} weight="fill"/>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Today · Family care</div>
                  <div className="font-heading text-lg text-slate-900">4 items · 2 people</div>
                </div>
              </div>
              <ul className="space-y-2.5">
                <MiniCard color="#DC2626" bg="#FEE2E2" Icon={Pill}     title="Amma · 8:00 AM"  sub="Metformin 500mg"/>
                <MiniCard color="#EC4899" bg="#FCE7F3" Icon={Baby}     title="Kiran · 24w"     sub="Kick counter · 5 today"/>
                <MiniCard color="#0F766E" bg="#CCFBF1" Icon={Syringe}  title="Achu · Tuesday"  sub="MMR booster reminder"/>
                <MiniCard color="#7C3AED" bg="#EDE9FE" Icon={Dog}      title="Bruno"           sub="Rabies · Aug 22"/>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES — responsive grid */}
      <section className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12 py-14 md:py-24">
        <div className="max-w-3xl">
          <Eyebrow color="#DC2626">What we offer</Eyebrow>
          <h2 className="font-heading text-3xl md:text-5xl mt-4 leading-tight text-slate-900">
            One app. Every care need. Every family member.
          </h2>
          <p className="text-slate-600 mt-4 md:mt-5 leading-relaxed">
            Rescigo Health brings verified doctors, nurses, pharmacies, ambulances
            and specialists together — with reminders that make sure nothing slips.
          </p>
        </div>
        <div className="mt-8 md:mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {SERVICES.map((s, i) => (
            <FeatureCard key={i} Icon={s.Icon} title={s.title} desc={s.desc} accent="#DC2626"/>
          ))}
        </div>
      </section>

      {/* REMINDERS strip */}
      <section className="bg-[#FFF7F5] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Eyebrow color="#DC2626">Smart reminders</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl mt-3 leading-tight text-slate-900">
              A gentle nudge — before you forget.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Voice-announced pill reminders, ovulation windows, kick counters,
              vaccine schedules and pet health — Rigo, our on-device assistant,
              speaks up so your family never misses a beat.
            </p>
          </div>
          <BulletList items={REMINDERS} color="#DC2626"/>
        </div>
      </section>

      {/* HOW IT WORKS — steps */}
      <section className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12 py-14 md:py-24">
        <Eyebrow color="#DC2626">How it works</Eyebrow>
        <h2 className="font-heading text-3xl md:text-4xl mt-3 leading-tight text-slate-900">
          Three steps to get started.
        </h2>
        <div className="mt-8 md:mt-12 grid sm:grid-cols-3 gap-4 md:gap-6">
          <Step n="01" Icon={PhoneCall}      title="Download & sign up"
                desc="Phone OTP, add your family circle in under 90 seconds."/>
          <Step n="02" Icon={CalendarCheck}  title="Set reminders"
                desc="Medicines, vaccines, appointments — for each loved one."/>
          <Step n="03" Icon={Truck}          title="Book care & pickups"
                desc="Doctors, nurses, ambulance and biomedical waste — in-app."/>
        </div>
      </section>

      {/* Trust badges */}
      <section className="bg-white pb-16">
        <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12 flex flex-wrap items-center justify-center gap-4 md:gap-6 text-[11px] uppercase tracking-[0.22em] text-slate-500">
          <span className="flex items-center gap-2"><Certificate size={13} weight="fill" className="text-[#DC2626]"/>CPCB compliant</span>
          <span>·</span><span>Verified doctors</span>
          <span>·</span><span>Licensed nurses</span>
          <span>·</span><span>24×7 SOS</span>
          <span>·</span><span>Kerala-wide</span>
        </div>
      </section>

      <CtaBanner
        eyebrow="Rescigo Health"
        title="Care that speaks up. Family that thanks you."
        body="Try the app free. All the reminders, none of the anxiety."
        primary={{ label: "Sign up free", to: "/register", testid: "health-cta-primary" }}
        secondary={{ label: "Book a call", to: "/contact", testid: "health-cta-secondary" }}
        accent="#DC2626"
      />

      <MarketingFooter/>
    </div>
  );
}

function MiniCard({ Icon, color, bg, title, sub }) {
  return (
    <li className="flex items-center gap-3 rounded-xl border border-slate-100 p-2.5">
      <div className="w-9 h-9 rounded-lg grid place-items-center flex-none" style={{ background: bg, color }}>
        <Icon size={16} weight="fill"/>
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-[13px] text-slate-900 truncate">{title}</div>
        <div className="text-[11px] text-slate-500 truncate">{sub}</div>
      </div>
    </li>
  );
}

function Step({ n, Icon, title, desc }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#FEE2E2] text-[#DC2626]">
          <Icon size={18} weight="duotone"/>
        </div>
        <div className="font-mono text-[11px] text-slate-400 tracking-[0.2em]">{n}</div>
      </div>
      <h3 className="font-heading text-lg text-slate-900">{title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}
